import { FORWARD_COST_PER_STEP, FORWARD_ORB_COST } from "@/program/Card";
import {
  OrbColor,
  ORB_COLORS,
  ORB_COLOR_LABELS,
} from "@/program/VariableState";

/**
 * 歩数選択の選択肢
 */
const STEP_OPTIONS = [1, 2, 3, 5, 10, 20, 50, 100] as const;

export type StepOption = (typeof STEP_OPTIONS)[number];

/**
 * 歩数選択の結果（数値 or オーブ参照）
 */
export type StepSelection =
  | { type: "steps"; value: number }
  | { type: "orb"; color: OrbColor };

/**
 * 歩数選択モーダル
 */
export class StepSelectorModal {
  private overlay: HTMLElement;
  private modal: HTMLElement;
  private onSelectCallback?: (selection: StepSelection) => void;
  private onCloseCallback?: () => void;
  private availableMP: number = Infinity;
  private isOpen: boolean = false;
  private previousFocus: HTMLElement | null = null;

  constructor() {
    // オーバーレイ
    this.overlay = document.createElement("div");
    this.overlay.className = "step-modal-overlay";
    this.overlay.addEventListener("click", () => this.close());

    // モーダル本体
    this.modal = document.createElement("div");
    this.modal.className = "step-modal";
    this.modal.setAttribute("role", "dialog");
    this.modal.setAttribute("aria-modal", "true");
    this.modal.setAttribute("aria-label", "移動マス数を選択");
    this.modal.addEventListener("click", (e) => e.stopPropagation());

    this.overlay.appendChild(this.modal);
  }

  /**
   * モーダルを開く
   */
  open(current: StepSelection | null, availableMP: number): void {
    if (this.isOpen) return;
    this.isOpen = true;

    this.availableMP = availableMP;
    this.previousFocus = document.activeElement as HTMLElement | null;
    this.render(current);
    document.body.appendChild(this.overlay);

    // ESCキーで閉じる / フォーカストラップ
    document.addEventListener("keydown", this.handleKeyDown);

    // 最初のフォーカス可能な要素にフォーカス
    requestAnimationFrame(() => {
      const first = this.modal.querySelector("button:not([disabled])");
      if (first instanceof HTMLElement) first.focus();
    });
  }

  /**
   * モーダルを閉じる
   */
  close(): void {
    if (!this.isOpen) return;
    this.isOpen = false;

    this.overlay.remove();
    document.removeEventListener("keydown", this.handleKeyDown);
    this.previousFocus?.focus();
    this.onCloseCallback?.();
  }

  /**
   * モーダルが開いているか
   */
  getIsOpen(): boolean {
    return this.isOpen;
  }

  /**
   * 選択時コールバック
   */
  onSelect(callback: (selection: StepSelection) => void): void {
    this.onSelectCallback = callback;
  }

  /**
   * 閉じた時コールバック
   */
  onClose(callback: () => void): void {
    this.onCloseCallback = callback;
  }

  /**
   * ESCキーハンドラ
   */
  private handleKeyDown = (e: KeyboardEvent): void => {
    if (e.key === "Escape") {
      this.close();
    } else if (e.key === "Tab") {
      this.trapFocus(e);
    }
  };

  /** モーダル内でフォーカスを循環させる */
  private trapFocus(e: KeyboardEvent): void {
    const focusable = this.modal.querySelectorAll("button:not([disabled])");
    if (focusable.length === 0) return;

    const first = focusable[0] as HTMLElement;
    const last = focusable[focusable.length - 1] as HTMLElement;

    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  }

  /**
   * モーダル内容を描画
   */
  private render(current: StepSelection | null): void {
    const children: HTMLElement[] = [];

    // タイトル
    const title = document.createElement("div");
    title.className = "step-modal__title";
    title.textContent = "移動マス数を選択";
    children.push(title);

    // 数値選択肢グリッド
    const grid = document.createElement("div");
    grid.className = "step-modal__grid";

    for (const steps of STEP_OPTIONS) {
      const cost = steps * FORWARD_COST_PER_STEP;
      const isDisabled = cost > this.availableMP;
      const isSelected =
        current !== null && current.type === "steps" && steps === current.value;

      const button = document.createElement("button");
      button.className = "step-modal__option";
      if (isSelected) button.classList.add("step-modal__option--selected");
      if (isDisabled) button.classList.add("step-modal__option--disabled");
      button.disabled = isDisabled;
      button.setAttribute(
        "aria-label",
        `${steps}マス（コスト${cost}）${isDisabled ? "MP不足" : ""}`
      );

      // 歩数
      const stepsSpan = document.createElement("span");
      stepsSpan.className = "step-modal__steps";
      stepsSpan.textContent = String(steps);
      button.appendChild(stepsSpan);

      // コスト
      const costSpan = document.createElement("span");
      costSpan.className = "step-modal__cost";
      costSpan.textContent = `${cost}MP`;
      button.appendChild(costSpan);

      if (!isDisabled) {
        button.addEventListener("click", () => {
          this.onSelectCallback?.({ type: "steps", value: steps });
          this.close();
        });
      }

      grid.appendChild(button);
    }

    children.push(grid);

    // オーブ選択肢セクション
    const orbSection = document.createElement("div");
    orbSection.className = "step-modal__orb-section";

    const orbLabel = document.createElement("div");
    orbLabel.className = "step-modal__orb-label";
    orbLabel.textContent = "変数で前進";
    orbSection.appendChild(orbLabel);

    const orbGrid = document.createElement("div");
    orbGrid.className = "step-modal__orb-grid";

    for (const color of ORB_COLORS) {
      const isDisabled = FORWARD_ORB_COST > this.availableMP;
      const isSelected =
        current !== null && current.type === "orb" && current.color === color;

      const button = document.createElement("button");
      button.className = "step-modal__orb-option";
      if (isSelected) button.classList.add("step-modal__orb-option--selected");
      if (isDisabled) button.classList.add("step-modal__orb-option--disabled");
      button.disabled = isDisabled;
      button.setAttribute(
        "aria-label",
        `${ORB_COLOR_LABELS[color]}マス前進（コスト${FORWARD_ORB_COST}）${isDisabled ? "MP不足" : ""}`
      );

      // 色インジケーター
      const indicator = document.createElement("span");
      indicator.className = `step-modal__orb-indicator orb-color--${color} orb-glow`;
      button.appendChild(indicator);

      // ラベル
      const label = document.createElement("span");
      label.className = "step-modal__orb-name";
      label.textContent = ORB_COLOR_LABELS[color];
      button.appendChild(label);

      // コスト
      const costSpan = document.createElement("span");
      costSpan.className = "step-modal__cost";
      costSpan.textContent = `${FORWARD_ORB_COST}MP`;
      button.appendChild(costSpan);

      if (!isDisabled) {
        button.addEventListener("click", () => {
          this.onSelectCallback?.({ type: "orb", color });
          this.close();
        });
      }

      orbGrid.appendChild(button);
    }

    orbSection.appendChild(orbGrid);
    children.push(orbSection);

    // 閉じるボタン
    const closeBtn = document.createElement("button");
    closeBtn.className = "step-modal__close";
    closeBtn.textContent = "キャンセル";
    closeBtn.addEventListener("click", () => this.close());
    children.push(closeBtn);

    this.modal.replaceChildren(...children);
  }
}
